import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/app/auth';
import { Badge } from '@/components/ui/Badge';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { useAirportCities } from '@/lib/api/airports';
import { dayOffset, sameDay } from '@/lib/clock';
import { cx } from '@/lib/cx';
import { PLANE_PATH } from '@/lib/plane';
import { cityOf } from '@/types/airport';
import type { FlightDetail, FlightHistoryRow, FlightPhase, FlightRow } from '@/types/flight';
import { ARCHIVE_SOURCE, phaseOf, routeScope, statusTone } from '@/types/flight';
import { ROLES_ADMIN_ONLY } from '@/types/user';
import { useFlightDetail } from './useFlightDetail';
import styles from './FlightPanel.module.css';

/**
 * One flight, beside the table.
 *
 * It answers three questions in the order they get asked: where is this flight, when
 * is it, and who last touched it. The illustration is the first of those — a reader
 * scanning a board wants "has it gone yet" before any field of it.
 *
 * The panel is driven by an id in the URL, so it survives a refresh and can be
 * linked to. It never navigates: the table stays where it is, because the next thing
 * a reader does is open the row below.
 */

/** The flight-path arc, in the SVG's own coordinates. */
const ARC = {
  from: { x: 46, y: 74 },
  control: { x: 170, y: 16 },
  to: { x: 294, y: 74 },
};
const ARC_PATH = `M ${ARC.from.x} ${ARC.from.y} Q ${ARC.control.x} ${ARC.control.y} ${ARC.to.x} ${ARC.to.y}`;

/**
 * How far along the arc each phase draws.
 *
 * Three coarse stops, not a fraction: the API reports no position, so anything
 * smoother would be invented. What a board actually needs from this is "not yet /
 * airborne / arrived", which is exactly three states.
 */
const PROGRESS: Record<FlightPhase, number> = {
  before: 0.04,
  air: 0.5,
  after: 1,
  cancelled: 0,
};

const pointOnArc = (t: number) => ({
  x: (1 - t) * (1 - t) * ARC.from.x + 2 * (1 - t) * t * ARC.control.x + t * t * ARC.to.x,
  y: (1 - t) * (1 - t) * ARC.from.y + 2 * (1 - t) * t * ARC.control.y + t * t * ARC.to.y,
});

const angleOnArc = (t: number) => {
  const dx = 2 * (1 - t) * (ARC.control.x - ARC.from.x) + 2 * t * (ARC.to.x - ARC.control.x);
  const dy = 2 * (1 - t) * (ARC.control.y - ARC.from.y) + 2 * t * (ARC.to.y - ARC.control.y);
  return (Math.atan2(dy, dx) * 180) / Math.PI;
};

interface FlightPanelProps {
  id: number;
  /** The rows on screen, so the other leg of this flight can be offered. */
  rows: readonly FlightRow[];
  onClose: () => void;
  onOpen: (id: number) => void;
}

export const FlightPanel = ({ id, rows, onClose, onOpen }: FlightPanelProps) => {
  const { flight, history, historyFailed, isLoading, notFound, errorKey } = useFlightDetail(id);
  const { t, formatDateLong } = useTranslation();

  /**
   * The day this flight belongs to.
   *
   * Scheduled first, because that is the day the board is showing. A row with no
   * schedule at all still has a day worth naming, so the two live values stand in.
   */
  const day = flight?.scheduledDate ?? flight?.estimatedDate ?? flight?.actualDate ?? null;

  /**
   * The same flight's other leg, if it is on this page.
   *
   * A flight is two rows and an operator often wants the other one — but only the
   * current page is in hand, so this appears when it can and stays silent when it
   * cannot. Fetching it would mean guessing at a second query for something the
   * reader may not want.
   *
   * MATCHING THE ROUTE AND THE NUMBER IS NOT ENOUGH, which is what this used to do.
   * A flight number and a route repeat every single day, so on a board covering
   * more than one day it offered the same leg of another day's operation: an IQ394
   * departure on the 27th pointing at the IQ394 departure on the 31st, both from
   * BXH, neither of them the other's leg. Two conditions are doing the work:
   *
   * - **The other flightType.** The pair is one DEPARTURE and one ARRIVAL — the
   *   departure row belongs to the origin, the arrival row to the destination — so
   *   a row sharing this one's type is another operation, never its sibling.
   * - **The same operation.** `departedTime` is the strong key: it is when the
   *   aircraft left its origin and is carried on BOTH legs, so two rows that agree
   *   on it are the same flight. Before departure nobody has one, and then the
   *   fallback is the rule the BACKEND itself joins legs by — scheduled within 24
   *   hours — because the two legs never share a scheduled time: one is the STD at
   *   the origin and the other the STA at the destination.
   *
   * The nearest candidate wins rather than the first in page order. A daily flight
   * puts the next day's operation just outside the window, and "just outside" is
   * not a margin to trust to the order rows happen to arrive in.
   */
  const sibling = useMemo(() => {
    if (!flight || flight.flightType === null) return null;

    const candidates = rows.filter(
      (row) =>
        row.id !== id &&
        row.flightNumber !== null &&
        row.flightNumber === flight.flightNumber &&
        row.departureAirportIataCode === flight.departureAirportIataCode &&
        row.arrivalAirportIataCode === flight.arrivalAirportIataCode &&
        row.flightType !== null &&
        row.flightType !== flight.flightType,
    );

    if (candidates.length === 0) return null;

    if (flight.departedTime !== null) {
      const sameAircraftMovement = candidates.find(
        (row) => row.departedTime === flight.departedTime,
      );
      if (sameAircraftMovement) return sameAircraftMovement;
    }

    const DAY_MS = 24 * 60 * 60 * 1000;

    return (
      candidates
        .flatMap((row) => {
          if (row.scheduledDate === null || flight.scheduledDate === null) return [];

          const gap = Math.abs(
            new Date(row.scheduledDate).getTime() - new Date(flight.scheduledDate).getTime(),
          );
          return Number.isNaN(gap) || gap > DAY_MS ? [] : [{ row, gap }];
        })
        .sort((left, right) => left.gap - right.gap)[0]?.row ?? null
    );
  }, [rows, flight, id]);

  return (
    <aside className={styles.panel} aria-label={t('schedule.detail.title')}>
      <header className={styles.header}>
        <div className={styles.identity}>
          {/* The day is said once, here. Every time below is then a time and nothing
              else — a card that repeats the date on three rows spends its width
              saying the same thing three times. */}
          <div className={styles.flightInfo}>
            <span className={styles.flightNumber}>{flight?.flightNumber ?? '—'}</span>
            {day !== null && <span className={styles.headerDate}>{formatDateLong(day)}</span>}  
          </div>

          <div className={styles.badges}>
            {flight?.flightType && (
              <Badge tone={flight.flightType === 'ARRIVAL' ? 'info' : 'neutral'}>
                {t(`schedule.types.${flight.flightType}`)}
              </Badge>
            )}
            {flight?.flightStatus && (
              <Badge tone={statusTone(flight.flightStatus)}>
                {t(`schedule.statuses.${flight.flightStatus}`)}
              </Badge>
            )}
            {/* Worked out from the two airport codes: the server's own flightScope
                only echoes the filter, so it cannot answer this. */}
            {flight && routeScope(flight) && (
              <Badge>
                {t(`schedule.scopes.${routeScope(flight) as 'DOMESTIC' | 'INTERNATIONAL'}`)}
              </Badge>
            )}
            {/* Only ever shown when false, because "active" is the normal case and a
                badge for it would be noise. */}
            {flight?.active === false && (
              <Badge tone="danger">{t('schedule.detail.withdrawn')}</Badge>
            )}
          </div>
        </div>

        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </header>

      {errorKey && (
        <p className={styles.error} role="alert">
          {t(errorKey)}
        </p>
      )}

      {isLoading && !flight && <p className={styles.message}>{t('common.loading')}</p>}

      {/* `200 null` rather than a 404 — the row is gone, or the id never existed. */}
      {notFound && <p className={styles.message}>{t('schedule.detail.notFound')}</p>}

      {flight && (
        <div className={styles.body}>
          <div className={styles.inner}>
            <Journey flight={flight} />
            <Times flight={flight} />
            <Facts flight={flight} />

            {sibling && (
              <button type="button" className={styles.sibling} onClick={() => onOpen(sibling.id)}>
                <span aria-hidden="true">⇄</span>
                {t('schedule.detail.otherLeg', {
                  leg: t(`schedule.types.${sibling.flightType ?? 'ARRIVAL'}`),
                  station: sibling.airportIataCode ?? '',
                })}
              </button>
            )}

            <h3 className={styles.sectionTitle}>
              {t('schedule.detail.history')}
              {history.length > 0 && <span className={styles.sectionCount}>{history.length}</span>}
            </h3>

            {historyFailed ? (
              <p className={styles.warning}>{t('schedule.detail.historyFailed')}</p>
            ) : history.length === 0 ? (
              <p className={styles.note}>{t('schedule.detail.noHistory')}</p>
            ) : (
              <>
                <ol className={styles.history}>
                  {history.map((event, index) => (
                    <HistoryEntry key={`${event.eventId}-${index}`} event={event} />
                  ))}
                </ol>
                {history.some((event) => event.source === ARCHIVE_SOURCE) && (
                  <p className={styles.note}>{t('schedule.detail.archiveNote')}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </aside>
  );
};

/** Where the flight is, drawn. */
const Journey = ({ flight }: { flight: FlightDetail }) => {
  const { t, locale, formatTime, formatDateTime } = useTranslation();

  // The two ends of this route, so each code can say which city it is. Already in
  // hand for a Kazakh airport, one request for a foreign one — see useAirportCities.
  const codes = useMemo(
    () => [flight.departureAirportIataCode, flight.arrivalAirportIataCode],
    [flight.departureAirportIataCode, flight.arrivalAirportIataCode],
  );
  const airportOf = useAirportCities(codes);

  const cityFor = (code: string | null): string => {
    const airport = airportOf(code);
    return airport ? cityOf(airport, locale) : '';
  };

  const fromCity = cityFor(flight.departureAirportIataCode);
  const toCity = cityFor(flight.arrivalAirportIataCode);

  /** The day the card is about, so a time on another one can say so. */
  const day = flight.scheduledDate ?? flight.estimatedDate ?? flight.actualDate ?? null;

  const phase = phaseOf(flight.flightStatus);
  const progress = PROGRESS[phase];
  const at = pointOnArc(Math.min(Math.max(progress, 0.04), 0.96));
  const angle = angleOnArc(Math.min(Math.max(progress, 0.04), 0.96));

  const cancelled = phase === 'cancelled';
  const arrived = phase === 'after';

  // Which end of this arc is the station whose board the row belongs to. An arrival
  // row is owned by its destination, a departure row by its origin.
  const stationIsDestination = flight.flightType === 'ARRIVAL';

  return (
    <div className={cx(styles.plot, cancelled && styles.cancelled)}>
      <svg
        className={styles.svg}
        viewBox="0 0 340 96"
        role="img"
        aria-label={t('schedule.detail.routeAlt', {
          from: flight.departureAirportIataCode ?? '',
          to: flight.arrivalAirportIataCode ?? '',
        })}
      >
        <line className={styles.ground} x1="16" y1="88" x2="324" y2="88" />

        {/* Both paths share one `pathLength="1"`, so the dash pattern IS the progress
            fraction rather than a length in user units. */}
        <path className={styles.routeRemaining} d={ARC_PATH} pathLength="1" />
        {progress > 0 && (
          <path
            className={styles.routeFlown}
            d={ARC_PATH}
            pathLength="1"
            strokeDasharray={`${progress} 1`}
          />
        )}

        <circle
          className={cx(styles.node, styles.nodeReached)}
          cx={ARC.from.x}
          cy={ARC.from.y}
          r="5"
        />
        <circle
          className={cx(styles.node, arrived ? styles.nodeReached : styles.nodePending)}
          cx={ARC.to.x}
          cy={ARC.to.y}
          r="5"
        />

        {!cancelled && (
          <g transform={`translate(${at.x} ${at.y}) rotate(${angle})`}>
            <path className={styles.planeHalo} d={PLANE_PATH} />
            <path className={styles.plane} d={PLANE_PATH} />
          </g>
        )}
      </svg>

      <div className={cx(styles.endpoint, styles.endpointFrom)}>
        <span className={styles.endpointCode}>{flight.departureAirportIataCode ?? '—'}</span>
        {fromCity && <span className={styles.endpointCity}>{fromCity}</span>}

        {/*
          On an arrival, when the inbound left its origin — the one thing about the
          far end of the route this row knows, and it belongs at that end rather than
          in a list of fields. On a departure it is not shown at all: there it is the
          same instant as the actual time three lines below.

          A time while the origin shares this card's day, the full stamp when it does
          not, because "left at 23:40" is a different fact depending on which day.
        */}
        {stationIsDestination && flight.departedTime ? (
          <span
            className={styles.endpointTime}
            title={`${t('schedule.detail.departedFrom')}: ${formatDateTime(flight.departedTime)}`}
          >
            {day !== null && sameDay(day, flight.departedTime)
              ? formatTime(flight.departedTime)
              : formatDateTime(flight.departedTime)}
          </span>
        ) : (
          <span className={styles.endpointNote}>
            {stationIsDestination ? '' : t('schedule.detail.thisStation')}
          </span>
        )}
      </div>

      <div className={cx(styles.endpoint, styles.endpointTo)}>
        <span className={styles.endpointCode}>{flight.arrivalAirportIataCode ?? '—'}</span>
        {toCity && <span className={styles.endpointCity}>{toCity}</span>}
        <span className={styles.endpointNote}>
          {stationIsDestination ? t('schedule.detail.thisStation') : ''}
        </span>
      </div>
    </div>
  );
};

/** Scheduled, estimated, actual — and how far off the schedule it ran. */
const Times = ({ flight }: { flight: FlightDetail }) => {
  const { t, formatTime, formatDateTime } = useTranslation();

  const scheduled = flight.scheduledDate;
  const moved = (value: string | null) =>
    scheduled !== null &&
    value !== null &&
    Math.abs(new Date(value).getTime() - new Date(scheduled).getTime()) >= 60_000;

  const cell = (labelKey: Key, value: string | null, highlight = false) => (
    <div className={styles.time}>
      <span className={styles.timeLabel}>{t(labelKey)}</span>
      {value ? (
        <span
          className={cx(styles.timeValue, highlight && moved(value) && styles.timeMoved)}
          title={formatDateTime(value)}
        >
          {formatTime(value)}
          {scheduled !== null && dayOffset(scheduled, value) !== 0 && (
            <sup className={styles.dayOffset}>
              {dayOffset(scheduled, value) > 0
                ? `+${dayOffset(scheduled, value)}`
                : dayOffset(scheduled, value)}
            </sup>
          )}
        </span>
      ) : (
        <span className={cx(styles.timeValue, styles.timeEmpty)}>—</span>
      )}
    </div>
  );

  return (
    <>
      {/* Times, not dates — the day is on the board above, and a `+1` appears when a
          time lands on another one. The labels are the panel's own, shorter than the
          column headings, which say "date" for what is shown here as a time. */}
      <div className={styles.times}>
        {cell('schedule.detail.times.scheduled', scheduled)}
        {cell('schedule.detail.times.estimated', flight.estimatedDate, true)}
        {cell('schedule.detail.times.actual', flight.actualDate, true)}
      </div>

      {flight.delayMinutes !== null && flight.delayMinutes !== 0 && (
        <div className={styles.delay}>
          <Badge tone={flight.delayMinutes > 0 ? 'warning' : 'success'}>
            {t('schedule.detail.delayed', { count: flight.delayMinutes })}
            {/* An IATA delay code. Shown raw: translating a code nobody memorises
                into a guess would be worse than the code itself. */}
            {flight.delayReason ? ` · ${flight.delayReason}` : ''}
          </Badge>
        </div>
      )}
    </>
  );
};

/**
 * The load: stand, who is on board, what is in the hold.
 *
 * Three fields, not ten. The three ids were here because the API returns them, which
 * is not a reason — nobody works a board by sender id, and ten identically shaped
 * rows mean reading all ten to find the two that matter. `created` and `updated`
 * are gone for a different reason: the history below is a dated list of exactly those
 * two facts, and it names the source of each.
 *
 * An empty field is dropped rather than shown as a dash, so a thin flight gets a
 * short card.
 */
const Facts = ({ flight }: { flight: FlightDetail }) => {
  const { t, formatNumber } = useTranslation();

  const facts: Array<{ labelKey: Key; value: ReactNode; mono?: boolean }> = [];

  if (flight.planeParkingName) {
    facts.push({ labelKey: 'schedule.columns.stand', value: flight.planeParkingName, mono: true });
  }

  if (flight.passengerAmount !== null) {
    facts.push({
      labelKey: 'schedule.detail.passengers',
      value: formatNumber(flight.passengerAmount),
    });
  }

  if (flight.totalCargoWeight !== null) {
    facts.push({
      labelKey: 'schedule.detail.cargo',
      value: `${formatNumber(flight.totalCargoWeight)} ${t('schedule.detail.kg')}`,
    });
  }

  if (facts.length === 0) return null;

  return (
    <dl className={styles.facts}>
      {facts.map((fact) => (
        <div className={styles.fact} key={fact.labelKey}>
          <dt className={styles.factLabel}>{t(fact.labelKey)}</dt>
          <dd className={cx(styles.factValue, fact.mono && styles.factMono)}>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
};

/** One change: when, by which integration, and what moved. */
const HistoryEntry = ({ event }: { event: FlightHistoryRow }) => {
  const { t, formatTime, formatDate, formatDateTime } = useTranslation();
  const { can } = useAuth();
  const [showRaw, setShowRaw] = useState(false);

  // The raw payload is the integration's own wire message — routing headers,
  // reference numbers, the lot. That is diagnostic material for an administrator,
  // not part of the change history a board reader follows, so for every other role
  // the entry simply ends at what changed.
  const mayReadRaw = can(ROLES_ADMIN_ONLY);

  const archived = event.source === ARCHIVE_SOURCE;
  const changes = event.changedFields ?? [];
  const marker =
    event.event === 'CREATED'
      ? styles.markerCreated
      : event.event === 'DELETED'
        ? styles.markerDeleted
        : styles.markerUpdated;

  return (
    <li className={cx(styles.event, archived && styles.archived)}>
      <span className={cx(styles.marker, marker)} aria-hidden="true" />

      <div className={styles.eventHead}>
        <span
          className={styles.eventTime}
          title={event.modifiedDate ? formatDateTime(event.modifiedDate) : undefined}
        >
          {event.modifiedDate ? formatTime(event.modifiedDate) : '—'}
        </span>
        <span className={styles.note}>
          {event.modifiedDate ? formatDate(event.modifiedDate) : ''}
        </span>
        {event.event && (
          <Badge
            tone={
              event.event === 'CREATED'
                ? 'success'
                : event.event === 'DELETED'
                  ? 'neutral'
                  : 'accent'
            }
          >
            {t(`schedule.detail.events.${event.event}`)}
          </Badge>
        )}
        <span
          className={styles.eventSource}
          title={archived ? t('schedule.detail.archivedRow') : undefined}
        >
          {event.source ?? t('schedule.detail.unknownSource')}
        </span>
      </div>

      {changes.length > 0 ? (
        <ul className={styles.changes}>
          {changes.map((change, index) => (
            <li className={styles.change} key={`${change.field}-${index}`}>
              <span className={styles.changeField}>{change.field}</span>
              <span className={styles.changeOld}>
                {change.oldValue ?? <span className={styles.empty}>—</span>}
              </span>
              <span className={styles.changeArrow} aria-hidden="true">
                →
              </span>
              <span className={styles.changeNew}>
                {change.newValue ?? <span className={styles.empty}>—</span>}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        event.details && <p className={styles.details}>{event.details}</p>
      )}

      {/*
        Only the button, and only when there is something behind it.

        The message reference and id used to sit here, and "no message" where there
        was none — three pieces of bookkeeping under every entry, on a list where the
        reader is following what changed. The reference is in the payload the button
        opens; "no message" was a label announcing an absence, which is the one thing
        an absence does not need.
      */}
      {mayReadRaw && event.raw && (
        <div className={styles.eventFoot}>
          <button
            type="button"
            className={styles.rawToggle}
            onClick={() => setShowRaw((shown) => !shown)}
          >
            {showRaw ? t('schedule.detail.hideRaw') : t('schedule.detail.showRaw')}
          </button>
        </div>
      )}

      {mayReadRaw && showRaw && event.raw && <pre className={styles.raw}>{event.raw}</pre>}
    </li>
  );
};
